import graphql from "babel-plugin-relay/macro";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchQuery, useRelayEnvironment } from "react-relay";

import { usePublicClubsSearchQuery } from "./__generated__/usePublicClubsSearchQuery.graphql";

export type PublicClubSearchResult = {
  id: string;
  slug: string;
  name: string;
  logoImage: { publicId: string } | null;
  backgroundImage: { publicId: string } | null;
  city: string;
  state: string;
};

const usePublicClubsSearchQueryString = graphql`
  query usePublicClubsSearchQuery($search: String!) {
    publicClubs(clubName: $search) {
      id
      slug
      name
      state
      city
      logoImage {
        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
        publicId: renderingKey
      }
      backgroundImage {
        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
        publicId: renderingKey
      }
    }
  }
`;

export const usePublicClubsSearch = (
  searchString: string
): { results: PublicClubSearchResult[]; isLoading: boolean } => {
  const env = useRelayEnvironment();
  const [results, setResults] = useState<PublicClubSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestSearchRef = useRef<string>("");

  const search = useMemo(
    () =>
      searchString && searchString.trim().length > 0 ? searchString : null,
    [searchString]
  );

  useEffect(() => {
    if (search) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => {
    // Clear any in-flight debounce
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // If empty, clear results and skip querying
    if (!search) {
      setResults([]);
      latestSearchRef.current = "";
      return;
    }

    latestSearchRef.current = search;
    const currentSearch = search;

    timeoutRef.current = setTimeout(() => {
      fetchQuery<usePublicClubsSearchQuery>(
        env,
        usePublicClubsSearchQueryString,
        {
          search: currentSearch,
        }
      )
        .toPromise()
        .then((data) => {
          if (!data || latestSearchRef.current !== currentSearch) {
            return;
          }
          const list =
            data.publicClubs
              ?.filter((n): n is NonNullable<typeof n> => Boolean(n))
              .map<PublicClubSearchResult>((n) => ({
                id: n.id ?? "",
                name: n.name ?? "",
                slug: n.slug ?? "",
                logoImage: n.logoImage
                  ? { publicId: n.logoImage.publicId ?? "" }
                  : null,
                backgroundImage: n.backgroundImage
                  ? { publicId: n.backgroundImage.publicId ?? "" }
                  : null,
                city: n.city ?? "",
                state: n.state ?? "",
              })) ?? [];
          setResults(list);
          setIsLoading(false);
        })
        .catch((error) => {
          // In case of error, do not crash component; just clear results
          if (latestSearchRef.current === currentSearch) {
            setResults([]);
          }
          setIsLoading(false);
        });
    }, 250);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    //disabling exhaustive deps because the env object is not stable and causes infinite re-renders in testing envs
  }, [search]); /*eslint-disable-line react-hooks/exhaustive-deps */

  return { results, isLoading };
};
